/**
 * ManagerOpsQueue — Testing Notes 12 · item #5
 * ---------------------------------------------
 * Manager Home is now an "ops queue" instead of a dashboard mirror. AI Insights
 * were removed for this role; in their place is a checklist of today's daily
 * ops tasks. Each tile deep-links to the relevant tool and shows real-time
 * status (done / partial / pending) from the extended
 * `/api/manager/dashboard/summary` payload.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import {
  BookOpen, Thermometer, Landmark, CheckCircle2, AlertCircle,
  ArrowRight, ClipboardCheck, Loader2,
} from "lucide-react";

const TASKS = [
  {
    key: "verify",
    icon: ClipboardCheck,
    title: "Verify HISABs",
    to: "/manager/verify",
    testId: "ops-task-verify",
    hint: "Approve or reject submitted shift reports.",
    status: (q) => (q.verify_queue > 0 ? "pending" : "done"),
    statusText: (q) => (q.verify_queue > 0 ? `${q.verify_queue} awaiting` : "Nothing pending"),
  },
  {
    key: "stock",
    icon: BookOpen,
    title: "Stock Register",
    to: "/dealer/stock-register",
    testId: "ops-task-stock",
    hint: "Log tank opening, receipts, meter sales, dip.",
    status: (q) => (q.tanks_total === 0 ? "done" : q.stock_register_done >= q.tanks_total ? "done" : q.stock_register_done > 0 ? "partial" : "pending"),
    statusText: (q) => (q.tanks_total === 0 ? "No tanks configured" : `${q.stock_register_done}/${q.tanks_total} tanks filed today`),
  },
  {
    key: "density",
    icon: Thermometer,
    title: "Temp & Density",
    to: "/dealer/density-register",
    testId: "ops-task-density",
    hint: "Morning · receipt · post-decantation readings.",
    status: (q) => (q.tanks_total === 0 ? "done" : q.density_register_done >= q.tanks_total ? "done" : q.density_register_done > 0 ? "partial" : "pending"),
    statusText: (q) => (q.tanks_total === 0 ? "No tanks configured" : `${q.density_register_done}/${q.tanks_total} tanks filed today`),
  },
  {
    key: "bank",
    icon: Landmark,
    title: "Bank Deposit",
    to: "/manager/bank-deposits",
    testId: "ops-task-bank",
    hint: "Verify today's cash deposit into the current a/c.",
    // Status derived separately from /api/manager/bank-deposits/today-status
    dynamic: true,
  },
];

const TONES = {
  done:    { chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300", ring: "border-emerald-500/40", icon: CheckCircle2 },
  partial: { chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300", ring: "border-amber-500/40", icon: AlertCircle },
  pending: { chip: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300", ring: "border-red-500/40", icon: AlertCircle },
  loading: { chip: "bg-secondary text-muted-foreground", ring: "border-border", icon: Loader2 },
};

export default function ManagerOpsQueue() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    Promise.all([
      api.get("/manager/dashboard/summary").catch(() => ({ data: null })),
      api.get("/manager/bank-deposits/today-status").catch(() => ({ data: null })),
    ]).then(([s, b]) => {
      if (dead) return;
      setSummary(s.data); setBank(b.data); setLoading(false);
    });
    return () => { dead = true; };
  }, []);

  const q = summary?.ops_queue || { tanks_total: 0, stock_register_done: 0, density_register_done: 0, verify_queue: 0 };
  const bankStatus = deriveBankStatus(bank);

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 shadow-sm"
      data-testid="manager-ops-queue"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-primary" />
          <div className="text-sm font-bold">{t("components.manageropsqueue.today_s_ops_queue")}</div>
        </div>
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
          {q.date || todayLabel()}
        </div>
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        {TASKS.map((t) => {
          let status = "loading";
          let statusText = "Loading…";
          if (!loading) {
            if (t.key === "bank") {
              status = bankStatus.status;
              statusText = bankStatus.text;
            } else if (t.status) {
              status = t.status(q);
              statusText = t.statusText(q);
            }
          }
          const tone = TONES[status] || TONES.loading;
          const StatusIcon = tone.icon;
          return (
            <Link
              key={t.key}
              to={t.to}
              data-testid={t.testId}
              className={"group relative flex flex-col gap-2 rounded-xl border-2 bg-background/60 p-3 transition-all hover:-translate-y-0.5 hover:shadow-sm " + tone.ring}
            >
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <t.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">{t.title}</div>
                  <div className="text-[10px] text-muted-foreground">{t.hint}</div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 -translate-x-0.5 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:text-primary group-hover:opacity-100" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black " + tone.chip} data-testid={`${t.testId}-status`}>
                  <StatusIcon className={"h-3 w-3 " + (status === "loading" ? "animate-spin" : "")} />
                  {statusText}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function todayLabel() {
  return new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
}

/**
 * Bank deposit status uses a separate endpoint that already understands
 * holidays / Sundays. We map its response into our 3-state UI.
 */
function deriveBankStatus(b) {
  if (!b) return { status: "loading", text: "Loading…" };
  if (b.is_bank_holiday) return { status: "done", text: b.holiday_reason || "Bank holiday" };
  if (b.deposit_recorded) return { status: "done", text: `₹${Number(b.deposit_amount_today || 0).toLocaleString("en-IN")} deposited` };
  const cash = Number(b.cash_in_hand || 0);
  if (cash <= 0) return { status: "done", text: "No cash to deposit" };
  return { status: "pending", text: `₹${cash.toLocaleString("en-IN")} to deposit` };
}
